import { type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@arco-design/web-react';

import { ConfigContext } from '@/components/ConfigProvider';
import { getModeMap } from '@/components/TopNav/components/ChangeMode';
import { useSystemMenuState, useTenantMenuState } from '@/hooks/useMenu';
import { getClusterPrefix } from '@/hooks/useMenu/clusterMenu';
import { getModule } from '@/hooks/useRouteScope';

import { ErrorPage } from '../errorPage';
import styles from '../style/index.module.less';

/** 403 页自动返回首页的倒计时秒数 */
const HOME_REDIRECT_SECONDS = 10;

export const useHomepath = () => {
  const navigate = useNavigate();
  const { mode, tenant, cluster, tenantCluster, tenantList, tenantClusters, clusterList } = useContext(ConfigContext);
  const { menu: systemMenu } = useSystemMenuState();
  const { menu: tenantMenu, loading: tenantMenuLoading } = useTenantMenuState();
  const module = getModule();
  const modeMap = getModeMap({
    systemMenu,
    clusterList,
    tenantList,
    tenant,
    tenantCluster,
    cluster,
    tenantClusters,
    tenantMenu: tenantMenuLoading ? undefined : tenantMenu,
  });
  const prePath = new URL(window.location.href).searchParams.get('prePath');
  const clusterPrefix = getClusterPrefix()?.prefix;
  const fallbackPath = modeMap[module] || '/home';

  return useCallback(() => {
    if (prePath) {
      navigate(prePath);
      return;
    }
    if (!mode) {
      navigate('/changeMode');
      return;
    }
    if (module === 'cluster' || module === 'wsp') {
      navigate(`/${clusterPrefix}/base-info`);
      return;
    }
    navigate(fallbackPath);
  }, [navigate, prePath, mode, module, clusterPrefix, fallbackPath]);
};

/** 返回门户首页按钮：展示倒计时，到期后跳转 /home */
export function BackHomeWithCountdown({ extra }: { extra?: ReactNode }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(HOME_REDIRECT_SECONDS);

  const goPortalHome = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (countdown <= 1) {
        goPortalHome();
        return;
      }
      setCountdown((value) => value - 1);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [goPortalHome, countdown]);

  return (
    <div className={styles['error-page-home-actions']}>
      {extra}
      <span className={styles['error-page-countdown']}>{t('components:redirect.to.homepage.countdown', { seconds: countdown })}</span>
      <Button type="primary" onClick={goPortalHome}>
        {t('common:back.to.homepage')}
      </Button>
    </div>
  );
}

export function Page403({ showBtn = true, desc = '', height = undefined }) {
  const { t } = useTranslation();
  return (
    <ErrorPage title="403" height={height} des={desc || t('components:resource.unavailable')} img="/images/img_403.png">
      {showBtn && <BackHomeWithCountdown />}
    </ErrorPage>
  );
}
